/** Shared wire protocol between web client and Durable Object. */

export interface RoomUser {
  id: string;
  username: string;
  color: string;
  avatarSeed: string;
}

/** Quote shown on a reply bubble. */
export interface ChatReplyRef {
  id: string;
  username: string;
  text: string;
}

/** emoji → list of USER IDS who reacted (user ids, not usernames — names
 * change; ids are stable and cannot be spoofed by renaming). */
export type ReactionMap = Record<string, string[]>;

/** Watch-together vs study (nerd) surface — shared per room. */
export type RoomMode = "watch" | "nerd";

export interface FlashCard {
  id: string;
  term: string;
  definition: string;
}

/** Deck editing lock — toggled by anyone in the room. */
export interface FlashLockState {
  locked: boolean;
}

/** Wall-clock-anchored playhead for synced YouTube rooms. */
export interface MediaPlayback {
  /** Position in seconds when `at` was recorded. */
  positionSec: number;
  /** `Date.now()` when this anchor was set. */
  at: number;
  playing: boolean;
}

/** What the room is playing. Global stays on curated HLS (`default`). */
export interface RoomMedia {
  kind: "default" | "youtube";
  /** 11-char YouTube video id when kind === "youtube". */
  youtubeId?: string;
  /** Shared playhead — only when kind === "youtube". */
  playback?: MediaPlayback;
}

/** Compute the expected playhead from a wall-clock anchor. */
export function expectedPlaybackPosition(
  pb: MediaPlayback,
  now = Date.now(),
): number {
  if (!pb.playing) return pb.positionSec;
  return pb.positionSec + (now - pb.at) / 1000;
}

export function createPlaybackAnchor(
  positionSec: number,
  playing: boolean,
  at = Date.now(),
): MediaPlayback {
  return {
    positionSec: Math.max(0, positionSec),
    at,
    playing,
  };
}

/** Per-user flashcard view — keyed by `RoomUser.id` in room_state. */
export interface FlashViewState {
  cardIndex: number;
  flipped: boolean;
  /** User id of the study leader being followed, if any. */
  followingUserId: string | null;
}

export interface RoomStatePayload {
  mode: RoomMode;
  cards: FlashCard[];
  /** Per-user card position + follow state (nerd mode). */
  views: Record<string, FlashViewState>;
  media: RoomMedia;
  /** Whether the shared deck is locked for editing. */
  flashLock?: FlashLockState;
  roomName: string;
  /** Room owner's user id (null in the global lobby / unowned rooms). */
  ownerId?: string | null;
  /** Whether the current owner holds the Pro entitlement (authoritative
   * moderation). Lets the client label owner actions honestly (instant vs
   * vote) instead of promising what the server will refuse. */
  ownerPro?: boolean;
  /** User ids currently muted by the owner (mute is a toggle). */
  mutedIds?: string[];
  /** Banned members (id + username recorded at ban time) — the /unban
   * picker needs names for users who are never in the room. */
  bannedUsers?: Array<{ id: string; username: string }>;
  /**
   * Server wall-clock ms at payload creation. Clients use it to estimate
   * their clock offset from the server so wall-clock-anchored playback
   * positions (MediaPlayback.at) don't inherit device clock skew.
   */
  serverTime: number;
  /** True when joiners must send a password. Never includes the secret. */
  passwordRequired?: boolean;
}

// ── Client → Server ──────────────────────────────────────────────

export interface JoinMessage {
  type: "join";
  connectionId: string;
  /** Stable, client-persisted identity key. Survives reconnects & reloads. */
  sessionId: string;
  username: string;
  /**
   * Client-persisted visual identity, sent so the server can silently rebuild
   * the same user after its session was garbage-collected during long idle.
   */
  color?: string;
  avatarSeed?: string;
  /** Persisted user id — kept stable across silent session rebuilds. */
  userId?: string;
  /**
   * Set on automatic reconnects (wake / same-tab reload / discard restore).
   * Tells the server this is NOT a new arrival: skip the "joined" line and
   * keep the session's current name instead of letting a stale tab overwrite
   * a rename done on another tab.
   */
  rejoin?: boolean;
  /** Required for a *new* session when the room has a password. */
  password?: string;
}

export interface ChatSendMessage {
  type: "chat";
  connectionId: string;
  sessionId: string;
  text: string;
  /** Id of the message being replied to, if any. */
  replyToId?: string;
}

export interface PingMessage {
  type: "ping";
  connectionId?: string;
}

export interface RenameMessage {
  type: "rename";
  connectionId: string;
  sessionId: string;
  username: string;
}

export interface RerollMessage {
  type: "reroll";
  connectionId: string;
  sessionId: string;
  avatarSeed?: string;
}

/** Sent when the user genuinely leaves (in-app Leave), for instant removal. */
export interface LeaveMessage {
  type: "leave";
  connectionId: string;
  sessionId: string;
}

/**
 * Tab is frozen / discarded / mobile-backgrounded — not a real close.
 * Next socket close for this session is silent (no "left" chat line).
 */
export interface ParkMessage {
  type: "park";
  connectionId: string;
  sessionId: string;
}

/** Toggle a reaction on a chat message. */
export interface ReactMessage {
  type: "react";
  connectionId: string;
  sessionId: string;
  messageId: string;
  emoji: string;
}

export interface SetModeMessage {
  type: "set_mode";
  connectionId: string;
  sessionId: string;
  mode: RoomMode;
}

export interface FlashAddMessage {
  type: "flash_add";
  connectionId: string;
  sessionId: string;
  term: string;
  definition: string;
}

/** Batch-add cards from pasted formatted text (parsed client-side). */
export interface FlashImportMessage {
  type: "flash_import";
  connectionId: string;
  sessionId: string;
  cards: Array<{ term: string; definition: string }>;
}

export interface FlashUpdateMessage {
  type: "flash_update";
  connectionId: string;
  sessionId: string;
  id: string;
  term: string;
  definition: string;
}

export interface FlashDeleteMessage {
  type: "flash_delete";
  connectionId: string;
  sessionId: string;
  id: string;
}

export interface FlashNavMessage {
  type: "flash_nav";
  connectionId: string;
  sessionId: string;
  /** Absolute index, or omit and use `delta`. */
  index?: number;
  delta?: number;
}

export interface FlashFlipMessage {
  type: "flash_flip";
  connectionId: string;
  sessionId: string;
}

/** Follow a user's card view (null target = study on your own). */
export interface FlashFollowMessage {
  type: "flash_follow";
  connectionId: string;
  sessionId: string;
  targetUserId: string | null;
}

/** Lock or unlock the shared deck for editing. */
export interface FlashLockMessage {
  type: "flash_lock";
  connectionId: string;
  sessionId: string;
  locked: boolean;
}

/** Private rooms only — set YouTube (or clear back to default stream). */
export interface SetMediaMessage {
  type: "set_media";
  connectionId: string;
  sessionId: string;
  /** Raw URL or 11-char id. Empty string clears to default. */
  youtube: string;
}

/** Update shared YouTube play/pause/seek (private rooms, YouTube active). */
export interface MediaSyncMessage {
  type: "media_sync";
  connectionId: string;
  sessionId: string;
  playing?: boolean;
  positionSec?: number;
}

/** Report a user's behavior to moderation. */
export interface ReportMessage {
  type: "report";
  connectionId: string;
  sessionId: string;
  /** User id of the reported person (from presence). */
  targetUserId: string;
  /** One of REPORT_REASONS. */
  reason: string;
}

/** Preset report reasons shown in the chat UI. */
export const REPORT_REASONS = [
  "harassment",
  "spam",
  "inappropriate",
  "other",
] as const;

// ── Slash system (Phase 1.3) ───────────────────────────────────────────

/** Room-owner moderation actions — /kick /mute /ban /unban, plus the
 * optional owner handoff (transfer is owner-only, single target). */
export type ModerationAction =
  | "kick"
  | "mute"
  | "unmute"
  | "ban"
  | "unban"
  | "transfer";

export interface CommandMessage {
  type: "command";
  connectionId: string;
  sessionId: string;
  action: ModerationAction;
  /** RoomUser.id targets (resolved against presence by the server). */
  targetIds: string[];
}

/** Visual-only whisper — everyone sees it, styled as a whisper. */
export interface WhisperMessage {
  type: "whisper";
  connectionId: string;
  sessionId: string;
  targetUserId: string;
  text: string;
}

export interface PollCreateMessage {
  type: "poll_create";
  connectionId: string;
  sessionId: string;
  title: string;
  options: string[];
}

export interface PollVoteMessage {
  type: "poll_vote";
  connectionId: string;
  sessionId: string;
  pollId: string;
  optionIndex: number;
}

export interface PollCloseMessage {
  type: "poll_close";
  connectionId: string;
  sessionId: string;
  pollId: string;
}

// ── Guest moderation votes (Phase 1.7) ────────────────────────────────
// Guests can't kick/ban authoritatively (that's the paid room-owner power);
// they start a room vote instead. Votes are private-rooms-only (the global
// lobby is strict and has no votes).

/** A room-wide vote to kick or ban a member. */
export interface VoteState {
  id: string;
  action: "kick" | "ban";
  targetUserId: string;
  targetName: string;
  /** User ids who voted yes (kick/ban) — one changeable vote per user. */
  yes: string[];
  /** User ids who voted no. */
  no: string[];
  createdBy: string;
  createdByName: string;
  createdAt: number;
  expiresAt: number;
  closed: boolean;
  /** Set when closed (expiry or decisive) — results stay visible briefly. */
  closedAt?: number;
  /** Final outcome (only meaningful when closed). */
  passed?: boolean;
}

export interface VoteCastMessage {
  type: "vote_cast";
  connectionId: string;
  sessionId: string;
  voteId: string;
  /** true = kick/ban, false = keep. */
  approve: boolean;
}

/** Owner sets or clears the room password (`/lock` / `/unlock`). */
export interface SetPasswordMessage {
  type: "set_password";
  connectionId: string;
  sessionId: string;
  /** Empty string clears the password. */
  password: string;
}

export type ClientMessage =
  | JoinMessage
  | ChatSendMessage
  | PingMessage
  | RenameMessage
  | RerollMessage
  | LeaveMessage
  | ParkMessage
  | ReactMessage
  | SetModeMessage
  | FlashAddMessage
  | FlashImportMessage
  | FlashUpdateMessage
  | FlashDeleteMessage
  | FlashNavMessage
  | FlashFlipMessage
  | FlashFollowMessage
  | FlashLockMessage
  | SetMediaMessage
  | MediaSyncMessage
  | ReportMessage
  | CommandMessage
  | WhisperMessage
  | PollCreateMessage
  | PollVoteMessage
  | PollCloseMessage
  | VoteCastMessage
  | SetPasswordMessage;

// ── Server → Client ──────────────────────────────────────────────

export interface ConnectedMessage {
  type: "connected";
  connectionId: string;
}

export interface IdentityMessage {
  type: "identity";
  user: RoomUser;
}

export interface ChatBroadcastMessage {
  type: "chat";
  /** Stable id — required for replies & reactions. */
  id: string;
  username: string;
  color: string;
  text: string;
  timestamp: number;
  replyTo?: ChatReplyRef;
  reactions?: ReactionMap;
}

export interface SystemMessage {
  type: "system";
  text: string;
  timestamp: number;
  /** Tag for client-side routing (e.g. "flash_lock" for floating popups). */
  kind?: string;
}

/** Unicast confirmation that a report was recorded (sent to the reporter only). */
export interface ReportAckMessage {
  type: "report_ack";
}

export interface PresenceMessage {
  type: "presence";
  users: RoomUser[];
  count: number;
}

export interface PongMessage {
  type: "pong";
}

export interface ErrorMessage {
  type: "error";
  text: string;
  waitMs?: number;
}

/** Exact join-gate copy — the client treats these as "stay on the overlay". */
export const ROOM_PASSWORD_WRONG = "Wrong room password";
export const ROOM_PASSWORD_REQUIRED = "This room needs a password";
export const ROOM_PASSWORD_SLOW = "Too many password attempts — wait a minute";

/**
 * Server is at capacity / under maintenance (cost gate, §3): the room is
 * closing — the client should show the branded capacity screen and stop
 * auto-reconnecting. Followed by a socket close (1013).
 */
export interface CapacityMessage {
  type: "capacity";
}

export interface UserUpdatedMessage {
  type: "user_updated";
  user: RoomUser;
}

/** Snapshot of recent chat, sent right after identity on (re)join. */
export interface HistoryMessage {
  type: "history";
  messages: ChatItem[];
  /**
   * When true, the client replaces on-screen chat instead of merging.
   * Used after an operator wipe so live viewers actually see a cleared room.
   */
  reset?: boolean;
}

/** Reaction map changed for a message. */
export interface ReactionMessage {
  type: "reaction";
  messageId: string;
  reactions: ReactionMap;
}

/** Shared room mode, deck, media — sent on join and after any change. */
export interface RoomStateMessage extends RoomStatePayload {
  type: "room_state";
}

// ── Slash system — server → client ────────────────────────────────────

/** A moderation action the room owner took (renders as a notice card). */
export interface ModerationMessage {
  type: "moderation";
  action: ModerationAction;
  actorName: string;
  targetNames: string[];
  timestamp: number;
}

/** Visual-only whisper broadcast (visible to everyone, styled as whisper). */
export interface WhisperBroadcastMessage {
  type: "whisper";
  id: string;
  username: string;
  color: string;
  targetName: string;
  text: string;
  timestamp: number;
}

/** Unicast: your mute state changed (mute is a toggle). */
export interface MutedMessage {
  type: "muted";
  muted: boolean;
  byName?: string;
}

/** Poll state broadcast — poll: null clears the panel. */
export interface PollMessage {
  type: "poll";
  poll: PollState | null;
}

/** Vote state broadcast — vote: null clears the panel. */
export interface VoteStateMessage {
  type: "vote_state";
  vote: VoteState | null;
}

export interface PollState {
  id: string;
  title: string;
  options: string[];
  /** votes[i] = user ids who voted option i (one per user, changeable). */
  votes: string[][];
  closed: boolean;
  /** Set when closed (button or expiry) — the panel keeps showing results
   * for a short window after this, then the server clears the poll. */
  closedAt?: number;
  createdBy: string;
  createdByName: string;
  expiresAt: number;
}

export type ServerMessage =
  | ConnectedMessage
  | IdentityMessage
  | ChatBroadcastMessage
  | SystemMessage
  | PresenceMessage
  | PongMessage
  | ErrorMessage
  | CapacityMessage
  | UserUpdatedMessage
  | HistoryMessage
  | ReactionMessage
  | RoomStateMessage
  | ReportAckMessage
  | ModerationMessage
  | WhisperBroadcastMessage
  | MutedMessage
  | PollMessage
  | VoteStateMessage;

/** Chat panel display item (chat, system, moderation notice, or whisper). */
export type ChatItem =
  | ChatBroadcastMessage
  | SystemMessage
  | ModerationMessage
  | WhisperBroadcastMessage;

/** Built-in reaction emoji set shown in the picker. */
export const REACTION_EMOJIS = ["❤️", "😂", "🔥", "👏", "👀"] as const;

const CLIENT_TYPES = new Set([
  "join",
  "chat",
  "ping",
  "rename",
  "reroll",
  "leave",
  "park",
  "react",
  "set_mode",
  "flash_add",
  "flash_import",
  "flash_update",
  "flash_delete",
  "flash_nav",
  "flash_flip",
  "flash_follow",
  "flash_lock",
  "set_media",
  "media_sync",
  "report",
  "command",
  "whisper",
  "poll_create",
  "poll_vote",
  "poll_close",
  "vote_cast",
  "set_password",
]);

export function isClientMessage(data: unknown): data is ClientMessage {
  if (!data || typeof data !== "object") return false;
  const type = (data as { type?: unknown }).type;
  return typeof type === "string" && CLIENT_TYPES.has(type);
}

export function parseServerMessage(raw: string): ServerMessage | null {
  try {
    const data = JSON.parse(raw) as ServerMessage;
    if (!data || typeof data !== "object" || typeof data.type !== "string") {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}
